import React, { useContext, useEffect, useState } from 'react';
import server from 'utils/server';
import Box from 'Components/Atoms/Box';
import Typography from 'Components/Atoms/Typography';
import Button, { ButtonVariants } from 'Components/Atoms/Button';
import { CircularProgress } from '@material-ui/core';
import { db } from 'utils/firebase';
import MissingInvoiceTemplateBanner from 'App/Settings/Components/MissingInvoiceTemplateBanner';
import { AuthContext } from 'Contexts/AuthContext';
import useMollieNavigation from 'CustomHooks/useMollieNavigation';

type LinkType =
  | 'self'
  | 'chargebacks'
  | 'customers'
  | 'invoices'
  | 'payments'
  | 'profiles'
  | 'refunds'
  | 'settlements'
  | 'dashboard'
  | 'documentation';

type MollieOrganization = {
  resource: 'organization';
  id: string;
  name: string;
  email: string;
  address: {
    streetAndNumber: string;
    postalCode: string;
    city: string;
    country: string;
  };
  registrationNumber: string;
  vatNumber: string;
  vatRegulation: 'shifted' | 'dutch';
  _links: Record<
    LinkType,
    { href: string; type: 'application/hal+json' | 'text|html' }
  >;
  onboarding: OnboardingStatus;
};

type OnboardingStatus = {
  resource: 'onboarding';
  name: string;
  signedUpAt: string;
  canReceivePayments: boolean;
  canReceiveSettlements: boolean;
  status: 'completed' | 'in-review' | 'needs-data';
};

export type MolliePageProps = {
  restaurantId: string;
};

const Mollie: React.FC<{
  restaurantId: string;
  buttonVariant?: ButtonVariants;
  showInvoiceTemplateWarning?: boolean;
}> = ({ restaurantId, buttonVariant = 'primary', showInvoiceTemplateWarning }) => {
  const mollieNavigation = useMollieNavigation();
  const { uid } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [mollieAccount, setMollieAccount] = useState<MollieOrganization | null>(
    null
  );
  const [error, seterror] = useState<string | null>(null);
  const [invoiceTemplateMissing, setInvoiceTemplateMissing] = useState(false);

  useEffect(() => {
    if (!showInvoiceTemplateWarning || !restaurantId) return;

    let cancelled = false;
    db.collection('restaurants')
      .doc(restaurantId)
      .collection('settings')
      .doc('invoice')
      .get()
      .then((doc) => {
        if (!cancelled) setInvoiceTemplateMissing(!doc.exists);
      })
      .catch(() => {
        if (!cancelled) setInvoiceTemplateMissing(false);
      });

    return () => {
      cancelled = true;
    };
  }, [restaurantId, showInvoiceTemplateWarning]);

  useEffect(() => {
    setLoading(true);
    setMollieAccount(null);
    seterror(null);

    if (!uid) return;

    let cancelled = false;

    server
      .get<MollieOrganization>(`/v03/mollie/organization/${restaurantId}`, {
        headers: { uid: uid || '' },
      })
      .then(async ({ data }) => {
        if (cancelled) return;
        setMollieAccount(data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status !== 404) {
          seterror(
            err?.response?.data?.message ??
              err?.response?.data ??
              'Es ist ein Fehler aufgetreten'
          );
        }
        setMollieAccount(null);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [restaurantId, uid]);

  return (
    <Box>
      {showInvoiceTemplateWarning &&
        !loading &&
        !!mollieAccount &&
        invoiceTemplateMissing && (
          <MissingInvoiceTemplateBanner restaurantId={restaurantId} />
        )}
      {!!error && (
        <Typography
          variant="text-3"
          color="error"
          block
          style={{ marginTop: 3, marginBottom: 17 }}
        >
          {error}
        </Typography>
      )}
      {loading && (
        <Box flex className="center">
          <CircularProgress />
        </Box>
      )}

      {!loading && !mollieAccount && !error && (
        <Button
          variant={buttonVariant}
          disabled={mollieNavigation.opening}
          loading={mollieNavigation.opening}
          onClick={() => mollieNavigation.open(restaurantId, 'connect')}
        >
          Connect Mollie Account
        </Button>
      )}
      {!loading &&
        !error &&
        !!mollieAccount &&
        (mollieAccount?.onboarding?.status !== 'needs-data' ? (
          <Button
            href={mollieAccount._links.dashboard.href}
            variant={buttonVariant}
            target="_blank"
            translation="settings"
          >
            Open Mollie Dashboard
          </Button>
        ) : (
          <>
            <Typography
              block
              variant="text-3"
              color="error"
              style={{ marginBottom: 8 }}
              translation="settings"
            >
              Not all data has yet been sent to Mollie. To accept payments,
              please complete the missing information by clicking on the button.
            </Typography>
            <Button
              href={mollieAccount._links.dashboard.href}
              variant={buttonVariant}
            >
              Continue
            </Button>
          </>
        ))}
    </Box>
  );
};

export default Mollie;
